import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { CampaignModel, campaignApi } from '@pukka/api';
import { toast } from '@pukka/toast';
import { Button, ScrollArea } from '@pukka/ui-components';
import { AxiosError } from 'axios';
import { isBefore } from 'date-fns';
import { FaTrashAlt } from 'react-icons/fa';

import { DetailedCampaignWithStatusAndLoadingFalse, State } from './CustomerDisplay.state';
import CustomerDisplayAlert from './CustomerDisplayAlert';
import CustomerDisplayForm from './CustomerDisplayForm';
import { CustomerDisplayFormSchema, CustomerDisplayFormSchemaType } from './customerDisplayFormSchema';
import CustomerDisplayReactivate from './CustomerDisplayReactivate';
import { constructPayload, generateErrorOutlets } from './utils';

type CustomerDisplayAlertType = 'edit' | 'delete' | 'deactivate' | 'reactivate';

async function editCampaign(data: CampaignModel.EditRequest) {
  return State.onUpdateCampaign(data);
}

async function deleteCampaign(id: CampaignModel.Detailed['id']) {
  return State.onDeleteCampaign(id);
}

async function deactivateCampaign(id: CampaignModel.Detailed['id']) {
  return State.onDeactivateCampaign(id);
}

async function reactivateCampaign(id: CampaignModel.Detailed['id']) {
  return State.onReactivateCampaign(id);
}

async function reactivateAndUpdateCampaign(data: CampaignModel.EditRequest) {
  return State.onReactivateAndUpdateCampaign(data);
}

interface CustomerDisplayEditBoxProps {
  readonly campaign: DetailedCampaignWithStatusAndLoadingFalse;
  readonly onClose: () => void;
}

function CustomerDisplayEditBox({ onClose, campaign }: CustomerDisplayEditBoxProps) {
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<CustomerDisplayAlertType>('edit');
  const [showReactivateModal, setShowReactivateModal] = useState(false);

  const [errorOutlets, setErrorOutlets] = useState([]);

  const methods = useForm<CustomerDisplayFormSchemaType>({
    resetOptions: {
      keepDirtyValues: true,
    },
    defaultValues: {
      name: campaign.name,
      images: campaign.images,
      duration: campaign.duration,
      setTimePeriod: !!campaign.end_date,
      timePeriod: {
        startDate: new Date(campaign.start_date),
        endDate: campaign.end_date ? new Date(campaign.end_date) : new Date(campaign.start_date),
      },
      outletIds: campaign.outlet_ids,
    },
    resolver: zodResolver(CustomerDisplayFormSchema),
  });

  const { handleSubmit, reset, getValues } = methods;

  useEffect(() => {
    reset({
      name: campaign.name,
      duration: campaign.duration,
      images: campaign.images,
      setTimePeriod: !!campaign.end_date,
      timePeriod: {
        startDate: new Date(campaign.start_date),
        endDate: campaign.end_date ? new Date(campaign.end_date) : new Date(campaign.start_date),
      },
      outletIds: campaign.outlet_ids,
    });
  }, [campaign, reset]);

  const handleShowAlert = (name: CustomerDisplayAlertType) => {
    setAlertType(name);
    setShowAlert(true);
  };

  const onSubmit = handleSubmit((data) => {
    const payload = { campaignId: campaign.id, campaignData: constructPayload(data, campaign.status) };

    return editCampaign(payload);
  });

  const handleClickReactivate = () => {
    if (campaign.end_date) {
      const isExpired = isBefore(new Date(campaign.end_date), new Date());

      if (isExpired) {
        setShowReactivateModal(true);
      } else {
        handleShowAlert('reactivate');
      }
    } else {
      handleShowAlert('reactivate');
    }
  };

  const handleReactivate = (setTimePeriod: boolean, startDate: Date, endDate: Date) => {
    const payload = {
      campaignId: campaign.id,
      campaignData: constructPayload({ ...getValues(), setTimePeriod, timePeriod: { startDate, endDate } }),
    };

    reactivateAndUpdateCampaign(payload)
      .then(() => {
        toast.success('Campaign was successfully reactivated');
        reset();
        onClose();
        setShowReactivateModal(false);
        campaignApi.invalidateCampaigns();
      })
      .catch(({ message }: AxiosError) => {
        toast.error(message);
      });
  };

  const handleConfirm = () => {
    let action;

    switch (alertType) {
      case 'edit':
        action = onSubmit;
        break;
      case 'delete':
        action = () => deleteCampaign(campaign.id);
        break;
      case 'deactivate':
        action = () => deactivateCampaign(campaign.id);
        break;
      case 'reactivate':
      default:
        action = () => reactivateCampaign(campaign.id);
        break;
    }

    if (action) {
      action()
        .then(() => {
          const status = alertType === 'edit' ? 'updated' : `${alertType}d`;

          setShowAlert(false);
          toast.success(`Campaign was successfully ${status}`);
          reset();
          onClose();
          campaignApi.invalidateCampaigns();
        })
        .catch((err: AxiosError<CampaignModel.CampaignErrorResponse>) => {
          if (err.response?.data?.code === '0010') {
            const outletList = generateErrorOutlets(err.message);

            setErrorOutlets(outletList);
          } else {
            toast.error(err.message);
          }
        });
    }
  };

  return (
    <>
      <form name="campaign-edit" className="relative h-full overflow-auto">
        <ScrollArea className="flex min-h-full flex-col overflow-auto bg-neutral-100 p-5 pb-32 md:pb-20">
          <div className="h-full rounded border border-neutral-200 bg-white">
            <FormProvider {...methods}>
              <CustomerDisplayForm errorOutlets={errorOutlets} />
            </FormProvider>
          </div>
        </ScrollArea>

        <CustomerDisplayAlert
          open={showAlert}
          variant={alertType}
          campaign={getValues()}
          onOpenChange={setShowAlert}
          onConfirm={handleConfirm}
        />
      </form>

      {showReactivateModal && (
        <CustomerDisplayReactivate open campaign={campaign} onConfirm={handleReactivate} onClose={() => setShowReactivateModal(false)} />
      )}

      <div className="absolute bottom-0 flex w-full items-center justify-end gap-x-2 bg-neutral-200 px-4 py-2.5">
        <Button aria-label="delete button" name="delete" variant="danger" onClick={() => handleShowAlert('delete')}>
          <FaTrashAlt size={14} />
        </Button>
        {campaign.status === 'inactive' ? (
          <Button aria-label="reactivate button" className="mr-auto" onClick={handleClickReactivate}>
            Reactivate
          </Button>
        ) : (
          <Button
            aria-label="deactivate button"
            name="deactivate"
            variant="danger"
            className="mr-auto"
            onClick={() => handleShowAlert('deactivate')}
          >
            Deactivate
          </Button>
        )}
        <Button variant="secondary" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button name="edit" onClick={handleSubmit(() => handleShowAlert('edit'))}>
          Save
        </Button>
      </div>
    </>
  );
}

export { CustomerDisplayEditBox as default, type CustomerDisplayAlertType };
